export type MeetingAudienceGroup =
  | 'principal'
  | 'safety-management'
  | 'contractor-representative'
  | 'committee-member';

export type MeetingAudiencePerson = Readonly<{
  identityId: string;
  displayName: string;
  contractorId: string | null;
  groups: readonly MeetingAudienceGroup[];
}>;

export type MeetingScheduleRow = Readonly<{
  meetingId: string;
  classification: Readonly<{
    kind: 'daily-safety' | 'committee';
    cadence: 'weekly' | 'monthly' | null;
  }>;
  startsAt: string;
  place: string;
  title: string;
  agenda: string;
  audienceSelection: Readonly<{
    groups: readonly MeetingAudienceGroup[];
    includeIdentityIds: readonly string[];
    excludeIdentityIds: readonly string[];
  }>;
  plannedAttendees: readonly MeetingAudiencePerson[];
  status: 'scheduled' | 'started' | 'cancelled';
  version: number;
  scheduleHash: string;
  minutesStatus: 'pending' | 'submitted' | 'returned' | 'completed';
  minutesVersion: number;
}>;

export type MeetingAttendanceRequestRow = Readonly<{
  requestId: string;
  meetingId: string;
  meetingVersion: number;
  meetingHash: string;
  attendee: MeetingAudiencePerson;
  requestedAt: string;
  status: 'requested';
  attempt: number;
}>;

export type MeetingScheduleWorkspace = Readonly<{
  siteId: string;
  timezone: string;
  audience: readonly MeetingAudiencePerson[];
  schedules: readonly MeetingScheduleRow[];
  attendanceRequests: readonly MeetingAttendanceRequestRow[];
  allowedActions: readonly string[];
  complete: boolean;
}>;

export type MeetingScheduleDraft = Readonly<{
  meetingId: string;
  classification: MeetingScheduleRow['classification'];
  startsAt: string;
  place: string;
  title: string;
  agenda: string;
  audienceSelection: MeetingScheduleRow['audienceSelection'];
}>;

type SchedulePort = Readonly<{
  loadSchedule(input: Readonly<{ siteId: string }>): Promise<unknown>;
  executeSchedule(input: Readonly<Record<string, unknown>>): Promise<unknown>;
}>;

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const records = (value: unknown) => (Array.isArray(value) ? value.map(record) : []);
const strings = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

function decodePerson(value: unknown): MeetingAudiencePerson {
  const item = record(value);
  return {
    identityId: String(item.identityId ?? ''),
    displayName: String(item.displayName ?? ''),
    contractorId: typeof item.contractorId === 'string' ? item.contractorId : null,
    groups: strings(item.groups) as MeetingAudienceGroup[],
  };
}

function decodeSchedule(value: unknown): MeetingScheduleRow {
  const item = record(value);
  const classification = record(item.classification);
  const selection = record(item.audienceSelection);
  const kind = classification.kind === 'committee' ? 'committee' : 'daily-safety';
  return {
    meetingId: String(item.meetingId ?? ''),
    classification: {
      kind,
      cadence:
        kind === 'committee' && classification.cadence === 'monthly' ? 'monthly' :
          kind === 'committee' ? 'weekly' : null,
    },
    startsAt: String(item.startsAt ?? ''),
    place: String(item.place ?? ''),
    title: String(item.title ?? ''),
    agenda: String(item.agenda ?? ''),
    audienceSelection: {
      groups: strings(selection.groups) as MeetingAudienceGroup[],
      includeIdentityIds: strings(selection.includeIdentityIds),
      excludeIdentityIds: strings(selection.excludeIdentityIds),
    },
    plannedAttendees: records(item.plannedAttendees).map(decodePerson),
    status: item.status === 'started' ? 'started' : item.status === 'cancelled' ? 'cancelled' : 'scheduled',
    version: Number(item.version ?? 0),
    scheduleHash: String(item.scheduleHash ?? ''),
    minutesStatus: item.minutesStatus === 'completed' ? 'completed' : item.minutesStatus === 'submitted' ? 'submitted' : item.minutesStatus === 'returned' ? 'returned' : 'pending',
    minutesVersion: Number(item.minutesVersion ?? 0),
  };
}

export function decodeMeetingScheduleWorkspace(value: unknown): MeetingScheduleWorkspace {
  const item = record(value);
  if (typeof item.siteId !== 'string' || !item.siteId || typeof item.timezone !== 'string' || !item.timezone)
    throw Error('MEETING_SCHEDULE_SCOPE_INCOMPLETE');
  return {
    siteId: item.siteId,
    timezone: item.timezone,
    audience: records(item.audience).map(decodePerson).filter((person) => person.identityId),
    schedules: records(item.schedules).map(decodeSchedule).filter((schedule) => schedule.meetingId),
    attendanceRequests: records(item.attendanceRequests).map((raw) => ({
      requestId: String(raw.requestId ?? ''),
      meetingId: String(raw.meetingId ?? ''),
      meetingVersion: Number(raw.meetingVersion ?? 0),
      meetingHash: String(raw.meetingHash ?? ''),
      attendee: decodePerson(raw.attendee),
      requestedAt: String(raw.requestedAt ?? ''),
      status: 'requested' as const,
      attempt: Number(raw.attempt ?? 1),
    })).filter((request) => request.requestId),
    allowedActions: strings(item.allowedActions),
    complete: item.complete === true,
  };
}

export interface MeetingScheduleRepository {
  load(): Promise<MeetingScheduleWorkspace>;
  save(draft: MeetingScheduleDraft, expectedVersion: number): Promise<void>;
  start(meetingId: string, expectedVersion: number): Promise<void>;
  cancel(meetingId: string, expectedVersion: number, reason: string): Promise<void>;
  respond(requestId: string, expectedAttempt: number, response: 'confirmed' | 'declined'): Promise<void>;
  resend(requestId: string, expectedAttempt: number): Promise<void>;
  completeMinutes(input: Readonly<{
    meetingId: string;
    expectedVersion: number;
    discussion: string;
    decisions: string;
    photoManifestIds: readonly string[];
  }>): Promise<void>;
  prepareMinutesReview(meetingId: string, expectedVersion: number): Promise<Readonly<Record<string, unknown>>>;
  decideMinutes(meetingId: string, expectedVersion: number, action: 'approve' | 'return', reviewEvidenceId: string, opinion: string): Promise<void>;
}

class FirebaseMeetingScheduleRepository implements MeetingScheduleRepository {
  constructor(private readonly port: Promise<SchedulePort>, private readonly siteId: string) {}

  async load() {
    const workspace = decodeMeetingScheduleWorkspace(await (await this.port).loadSchedule({ siteId: this.siteId }));
    if (workspace.siteId !== this.siteId) throw Error('MEETING_SCHEDULE_SCOPE_MISMATCH');
    return workspace;
  }

  private async execute(
    operation: string,
    aggregateId: string,
    expectedVersion: number,
    payload: Readonly<Record<string, unknown>>,
  ) {
    const key = crypto.randomUUID();
    await (await this.port).executeSchedule({
      operation,
      siteId: this.siteId,
      commandId: `meeting-schedule-${key}`,
      idempotencyKey: `meeting-schedule-${key}`,
      aggregateId,
      expectedVersion,
      payload,
    });
  }

  private async executeResult(operation: string, aggregateId: string, expectedVersion: number,
    payload: Readonly<Record<string, unknown>>) {
    const key = crypto.randomUUID();
    return record(await (await this.port).executeSchedule({ operation, siteId: this.siteId,
      commandId: `meeting-schedule-${key}`, idempotencyKey: `meeting-schedule-${key}`,
      aggregateId, expectedVersion, payload }));
  }

  save(draft: MeetingScheduleDraft, expectedVersion: number) {
    return this.execute('meeting-schedule.upsert', draft.meetingId, expectedVersion, {
      ...draft,
      startsAt: new Date(draft.startsAt).toISOString(),
    });
  }
  start(meetingId: string, expectedVersion: number) {
    return this.execute('meeting-schedule.start', meetingId, expectedVersion, {});
  }
  cancel(meetingId: string, expectedVersion: number, reason: string) {
    return this.execute('meeting-schedule.cancel', meetingId, expectedVersion, { reason });
  }
  respond(requestId: string, expectedAttempt: number, response: 'confirmed' | 'declined') {
    return this.execute('meeting-attendance.respond', requestId, expectedAttempt, { response });
  }
  resend(requestId: string, expectedAttempt: number) {
    return this.execute('meeting-attendance.resend', requestId, expectedAttempt, {});
  }
  completeMinutes(input: Readonly<{
    meetingId: string;
    expectedVersion: number;
    discussion: string;
    decisions: string;
    photoManifestIds: readonly string[];
  }>) {
    return this.execute('meeting-minutes.complete', input.meetingId, input.expectedVersion, {
      finalReview: true,
      scheduleId: input.meetingId,
      discussion: input.discussion,
      decisions: input.decisions,
      photoManifestIds: input.photoManifestIds,
    });
  }
  prepareMinutesReview(meetingId: string, expectedVersion: number) {
    return this.executeResult('meeting-minutes.prepare-review', meetingId, expectedVersion, {});
  }
  decideMinutes(meetingId: string, expectedVersion: number, action: 'approve' | 'return', reviewEvidenceId: string, opinion: string) {
    return this.execute(`meeting-minutes.${action}`, meetingId, expectedVersion, { reviewEvidenceId, opinion });
  }
}

export function createMeetingScheduleRepository(siteId: string): MeetingScheduleRepository {
  const config = {
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
  };
  if (!siteId || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId) ||
      import.meta.env.VITE_CONTENT_DATA_ADAPTER !== 'firebase' || Object.values(config).some((value) => !value.trim())) {
    return {
      load: () => Promise.reject(new Error('회의 일정 연결을 확인할 수 없습니다.')),
      save: () => Promise.reject(new Error('회의 일정을 저장할 수 없습니다.')),
      start: () => Promise.reject(new Error('회의를 시작할 수 없습니다.')),
      cancel: () => Promise.reject(new Error('회의를 취소할 수 없습니다.')),
      respond: () => Promise.reject(new Error('참석을 확인할 수 없습니다.')),
      resend: () => Promise.reject(new Error('참석 요청을 다시 보낼 수 없습니다.')),
      completeMinutes: () => Promise.reject(new Error('공식 회의록을 만들 수 없습니다.')),
      prepareMinutesReview: () => Promise.reject(new Error('회의록 검토본을 열 수 없습니다.')),
      decideMinutes: () => Promise.reject(new Error('회의록을 최종 검토할 수 없습니다.')),
    };
  }
  return new FirebaseMeetingScheduleRepository(
    import('@kop/operations-firebase-adapter').then((module) => module.createMeetingMinutesOperationsPort(config)),
    siteId,
  );
}
